import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const SongResultCard = ({ song, type, onRetry, onManualMatch }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusIcon = () => {
    switch (type) {
      case 'successful':
        return 'CheckCircle';
      case 'failed':
        return 'XCircle';
      case 'duplicate':
        return 'Copy';
      case 'manual':
        return 'AlertTriangle';
      default:
        return 'Music';
    }
  };

  const getStatusColor = () => {
    switch (type) {
      case 'successful':
        return 'text-success';
      case 'failed':
        return 'text-error';
      case 'duplicate':
        return 'text-warning';
      case 'manual':
        return 'text-warning';
      default:
        return 'text-text-primary';
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getMatchConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="p-4 bg-surface rounded-lg border border-border hover:bg-surface-secondary transition-colors duration-200">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <Image
            src={song.albumArt || `https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop`}
            alt={`${song.title} album art`}
            className="w-12 h-12 rounded-lg object-cover"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name={getStatusIcon()} size={16} className={getStatusColor()} />
                <h3 className="text-sm font-medium text-text-primary truncate">
                  {song.title}
                </h3>
              </div>
              <p className="text-sm text-text-secondary truncate">{song.artist}</p>
              <div className="flex items-center space-x-4 mt-2 text-xs text-text-muted">
                <span>{song.album}</span>
                <span>{formatDuration(song.duration)}</span>
                {song.matchConfidence && (
                  <span className={`font-medium ${getMatchConfidenceColor(song.matchConfidence)}`}>
                    {song.matchConfidence}% match
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              {type === 'failed' && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="RotateCcw"
                    iconSize={14}
                    onClick={() => onRetry(song.id)}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Search"
                    iconSize={14}
                    onClick={() => onManualMatch(song.id)}
                  >
                    Manual Match
                  </Button>
                </>
              )}
              {type === 'manual' && (
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Search"
                  iconSize={14}
                  onClick={() => onManualMatch(song.id)}
                >
                  Review
                </Button>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 rounded-md hover:bg-surface-secondary transition-colors duration-200"
              >
                <Icon 
                  name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="text-text-muted"
                />
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-text-muted mb-1">Original Platform</p>
                  <p className="text-text-secondary">{song.sourcePlatform}</p>
                </div>
                <div>
                  <p className="text-text-muted mb-1">Target Platform</p>
                  <p className="text-text-secondary">{song.targetPlatform}</p>
                </div>
                {song.failureReason && (
                  <div className="md:col-span-2">
                    <p className="text-text-muted mb-1">Failure Reason</p>
                    <p className="text-error">{song.failureReason}</p>
                  </div>
                )}
                {song.matchedSong && (
                  <div className="md:col-span-2">
                    <p className="text-text-muted mb-1">Matched Song</p>
                    <p className="text-text-secondary">
                      {song.matchedSong.title} by {song.matchedSong.artist}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-text-muted mb-1">Sync Time</p>
                  <p className="text-text-secondary">
                    {new Date(song.syncTime).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SongResultCard;